import { useState, useCallback, useEffect, useRef } from 'react'
import { Loader2, AlertTriangle, Film, Minimize2 } from 'lucide-react'
import { usePlaybackStore, useSettingsStore } from '../../store'
import { useVideoPlayer } from '../../hooks/useVideoPlayer'
import { useFullscreen } from '../../hooks/useFullscreen'
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts'
import { VideoControls } from '../VideoControls'
import { RoomBrand } from '../RoomBrand'
import { OverflowMarquee } from '../OverflowMarquee'
import { BROADCAST_OUTPUT_CHANNEL, cleanFilename } from '../../utils'
import { broadcastApi } from '../../services/api'
import type { CloudProvider } from '../../types'
import clsx from 'clsx'

interface VideoPlayerProps {
  provider: CloudProvider | null
  appName: string
  welcomeMessage: string | null
  isOwner: boolean
  canSavePlayback: boolean
  isBroadcastMode?: boolean
}

type BroadcastCommand =
  | { type: 'play-video'; videoId: string }
  | { type: 'toggle-play' }
  | { type: 'seek'; seconds: number }
  | { type: 'volume'; volume: number; muted: boolean }

export function VideoPlayer({
  provider,
  appName,
  welcomeMessage,
  isOwner,
  canSavePlayback,
  isBroadcastMode = false,
}: VideoPlayerProps) {
  const [controlsVisible, setControlsVisible] = useState(true)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerHoverRef = useRef(false)
  const broadcastRef = useRef<BroadcastChannel | null>(null)
  const broadcastPublishTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const {
    status,
    errorMessage,
    currentVideoId,
    currentIndex,
    playlist,
    isTheaterMode,
    nextVideo,
    previousVideo,
    setCurrentVideoById,
    toggleTheaterMode,
    setTheaterMode,
    volume,
    muted,
    setVolume,
    setMuted,
  } = usePlaybackStore()

  const roomId = useSettingsStore(state => state.settings?.roomId ?? null)
  const { videoRef, togglePlay, seek, seekRelative, previewSource } = useVideoPlayer(provider, canSavePlayback, isOwner, roomId)
  const { containerRef, toggleFullscreen } = useFullscreen()

  const currentVideo = currentIndex >= 0 ? playlist[currentIndex] : null
  const isLoading = status === 'loading' || status === 'buffering'
  const isError = status === 'error'
  const isIdle = status === 'idle' && !currentVideoId

  const sendBroadcastCommand = useCallback((command: BroadcastCommand) => {
    if (isBroadcastMode) return
    broadcastRef.current?.postMessage(command)
  }, [isBroadcastMode])

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return

    const channel = new BroadcastChannel(BROADCAST_OUTPUT_CHANNEL)
    broadcastRef.current = channel

    if (isBroadcastMode) {
      channel.onmessage = (event: MessageEvent<BroadcastCommand>) => {
        const command = event.data
        if (command.type === 'play-video') {
          setCurrentVideoById(command.videoId)
          return
        }
        if (command.type === 'toggle-play') {
          togglePlay()
          return
        }
        if (command.type === 'seek') {
          seek(command.seconds)
          return
        }
        if (command.type === 'volume') {
          setVolume(command.volume)
          setMuted(command.muted)
        }
      }
    }

    return () => {
      channel.close()
      if (broadcastRef.current === channel) broadcastRef.current = null
    }
  }, [isBroadcastMode, seek, setCurrentVideoById, setMuted, setVolume, togglePlay])

  useEffect(() => {
    if (isBroadcastMode || !currentVideoId) return
    sendBroadcastCommand({ type: 'play-video', videoId: currentVideoId })
  }, [currentVideoId, isBroadcastMode, sendBroadcastCommand])

  useEffect(() => {
    sendBroadcastCommand({ type: 'volume', volume, muted })
  }, [muted, sendBroadcastCommand, volume])

  useEffect(() => {
    if (isBroadcastMode || !isOwner) return
    const publish = () => {
      const playback = usePlaybackStore.getState()
      void broadcastApi.publishState({
        videoId: playback.currentVideoId,
        isPlaying: playback.status === 'playing' || playback.status === 'buffering',
        position: playback.position,
        observedAt: Date.now(),
        volume: playback.volume,
        muted: playback.muted,
      }).catch(() => {
        // A local broadcast view still works if the public token has not been enabled.
      })
    }
    publish()
    if (status === 'playing' || status === 'buffering') {
      broadcastPublishTimerRef.current = setInterval(publish, 1_000)
    }
    return () => {
      if (broadcastPublishTimerRef.current) clearInterval(broadcastPublishTimerRef.current)
      broadcastPublishTimerRef.current = null
    }
  }, [currentVideoId, isBroadcastMode, isOwner, muted, status, volume])

  const handleTogglePlay = useCallback(() => {
    togglePlay()
    sendBroadcastCommand({ type: 'toggle-play' })
  }, [sendBroadcastCommand, togglePlay])

  const handleSeek = useCallback((seconds: number) => {
    seek(seconds)
    sendBroadcastCommand({ type: 'seek', seconds })
  }, [seek, sendBroadcastCommand])

  // ─── Controls auto-hide ───────────────────────────────────────────────────

  const showControls = useCallback(() => {
    setControlsVisible(true)
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    if (containerHoverRef.current && status === 'playing') {
      hideTimerRef.current = setTimeout(() => setControlsVisible(false), 3000)
    }
  }, [status])

  useEffect(() => {
    if (status !== 'playing') {
      setControlsVisible(true)
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    } else {
      hideTimerRef.current = setTimeout(() => setControlsVisible(false), 3000)
    }
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    }
  }, [status])

  // ─── Keyboard shortcuts ───────────────────────────────────────────────────

  useKeyboardShortcuts({
    onTogglePlay: togglePlay,
    onSeekRelative: seekRelative,
    onNextVideo: nextVideo,
    onPreviousVideo: previousVideo,
    onToggleTheaterMode: toggleTheaterMode,
  })

  return (
    <div
      ref={containerRef}
      className={clsx(
        'relative w-full h-full flex flex-col overflow-hidden',
        isBroadcastMode ? 'bg-black' : 'bg-void',
      )}
      onMouseMove={showControls}
      onMouseEnter={() => { containerHoverRef.current = true }}
      onMouseLeave={() => { containerHoverRef.current = false; setControlsVisible(true) }}
    >
      {/* Video element */}
      <div className="flex-1 video-wrapper" onClick={togglePlay}>
        <video
          ref={videoRef}
          className={clsx(
            'w-full h-full object-contain',
            (isIdle || isError) && 'opacity-0',
          )}
          playsInline
          autoPlay={false}
          preload="metadata"
        />

        <div className="room-signature">
          <RoomBrand name={appName} variant="overlay" />
        </div>

        {/* Empty state */}
        {isIdle && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-muted">
            <Film size={48} strokeWidth={1} />
            <div className="max-w-md px-8 text-center">
              <p className="text-sm text-soft">
                {isBroadcastMode
                  ? 'Broadcast output waiting for playback'
                  : welcomeMessage || 'Select a video from the playlist'}
              </p>
            </div>
          </div>
        )}

        {/* Loading spinner */}
        {isLoading && (
          <div className="absolute inset-0 flex items-end overflow-hidden bg-black">
            {currentVideo?.thumbnailUrl && (
              <img
                src={currentVideo.thumbnailUrl}
                alt=""
                className="absolute inset-0 h-full w-full scale-110 object-cover opacity-30 blur-xl"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/65 to-black/15" />
            <div className="relative z-10 w-full px-7 pb-24 sm:px-12 sm:pb-32">
              <div className="flex max-w-2xl items-end gap-5">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl border border-white/15 bg-white/10 shadow-2xl backdrop-blur-md">
                  <Loader2 size={25} className="animate-spin text-accent" />
                </div>
                <div className="min-w-0">
                  <p className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-accent">Next up</p>
                  <p className="truncate text-xl font-semibold tracking-tight text-white sm:text-3xl">
                    {currentVideo ? cleanFilename(currentVideo.name) : 'Preparing your next video'}
                  </p>
                  <p className="mt-2 text-sm text-white/65">Preparing playback…</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error state */}
        {isError && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-center px-8">
              <AlertTriangle size={36} className="text-danger" />
              <p className="text-sm text-soft">{errorMessage ?? 'Playback error'}</p>
              <p className="text-xs text-muted">Skipping to next video…</p>
            </div>
          </div>
        )}

        {/* Now playing title (shows briefly on video change) */}
        {currentVideo && !isIdle && (
          <div className={clsx(
            'player-now-playing absolute left-5 right-5 transition-opacity duration-500',
            controlsVisible ? 'opacity-100' : 'opacity-0',
          )}>
            <OverflowMarquee
              text={cleanFilename(currentVideo.name)}
              className="text-xl font-semibold tracking-tight text-bright drop-shadow-lg sm:text-2xl"
            />
            <p className="text-xs text-dim mt-0.5">
              {isBroadcastMode ? 'Broadcast output' : `${currentIndex + 1} of ${playlist.length}`}
            </p>
          </div>
        )}

        {isTheaterMode && !isBroadcastMode && controlsVisible && (
          <button
            onClick={(event) => {
              event.stopPropagation()
              setTheaterMode(false)
            }}
            className="absolute top-4 right-4 btn-ghost p-2 rounded-lg bg-black/45 backdrop-blur-sm"
            title="Exit theater (Esc)"
          >
            <Minimize2 size={15} />
          </button>
        )}
      </div>

      {/* Controls overlay */}
      <div className={clsx(
        'absolute bottom-0 left-0 right-0 transition-opacity duration-300',
        controlsVisible || status !== 'playing' ? 'opacity-100' : 'opacity-0',
        'pointer-events-none',
      )}>
        <div className={clsx(!controlsVisible && status === 'playing' ? 'pointer-events-none' : 'pointer-events-auto')}>
          <VideoControls
            onTogglePlay={handleTogglePlay}
            onSeek={handleSeek}
            onNext={nextVideo}
            onPrevious={previousVideo}
            onToggleFullscreen={toggleFullscreen}
            isBroadcastMode={isBroadcastMode}
            previewSource={previewSource}
          />
        </div>
      </div>
    </div>
  )
}
